import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, BRAND } from "@/lib/api";
import PrintHeader from "@/components/PrintHeader";
import LetterheadToggle, { useLetterheadMode } from "@/components/LetterheadToggle";

/**
 * GST-compliant pharmacy tax invoice — A4 portrait.
 * Intra-state CGST + SGST split (each = gst_percent / 2). For inter-state we'd
 * switch to IGST — UI flag can be added later when the hospital supplies state code.
 */
export default function PrintPharmacyInvoice() {
  const { id } = useParams();
  const [s, setS] = useState(null);
  const [hospital, setHospital] = useState(null);
  const [branch, setBranch] = useState(null);
  const letterhead = useLetterheadMode();

  useEffect(() => {
    (async () => {
      const { data } = await api.get(`/pharmacy/sales/${id}`);
      setS(data);
      const me = await api.get("/auth/me");
      setHospital(me.data.hospital);
      setBranch(me.data.branch);
      const auto = new URLSearchParams(window.location.search).get("autoprint");
      if (auto !== "0") setTimeout(() => window.print(), 600);
    })();
  }, [id]);

  if (!s) return <div className="p-10 text-sm text-ink-muted">Loading…</div>;

  // Number-to-words for INR (handles up to 9,99,99,999)
  const inWords = (n) => {
    const ones = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine",
                  "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
    const tens = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];
    const two = (x) => x < 20 ? ones[x] : (tens[Math.floor(x / 10)] + (x % 10 ? " " + ones[x % 10] : ""));
    const three = (x) => {
      const h = Math.floor(x / 100), rest = x % 100;
      return (h ? ones[h] + " Hundred" + (rest ? " " : "") : "") + (rest ? two(rest) : "");
    };
    const rupees = Math.floor(n);
    if (rupees === 0) return "Zero";
    const crore = Math.floor(rupees / 10000000);
    const lakh  = Math.floor((rupees % 10000000) / 100000);
    const thou  = Math.floor((rupees % 100000) / 1000);
    const rest  = rupees % 1000;
    return [
      crore && two(crore) + " Crore",
      lakh  && two(lakh)  + " Lakh",
      thou  && two(thou)  + " Thousand",
      rest  && three(rest),
    ].filter(Boolean).join(" ").trim();
  };

  const saleTypeLabel = {
    retail: "Retail / Walk-in",
    opd:    "OPD Patient",
    ipd:    "IPD Patient",
  }[s.sale_type] || s.sale_type;

  return (
    <div className="pharm-a4 mx-auto bg-white p-8 text-sm text-ink" data-testid="print-pharm-invoice">
      <style>{`
        @page { size: A4 portrait; margin: 10mm; }
        .pharm-a4 { width: 210mm; max-width: 210mm; }
        @media print { html, body { background: white; margin: 0 !important; } .pharm-a4 { box-shadow: none !important; } }
      `}</style>

      {/* Header */}
      <LetterheadToggle />
      {!letterhead && (
        <PrintHeader
          hospital={hospital}
          branch={branch}
          subtitle="Pharmacy · Tax Invoice (GST)"
          right={<>
            <div>Invoice: <span className="font-mono font-semibold">{s.invoice_no}</span></div>
            <div>Date: {(s.created_at || "").slice(0, 16).replace("T", " ")}</div>
            <div className="mt-1 inline-block rounded bg-secondary px-1.5 py-0.5 text-[10px] font-semibold uppercase text-ink">{saleTypeLabel}</div>
          </>}
        />
      )}

      {/* Customer / Patient block */}
      <div className="mt-3 rounded border border-border bg-secondary/30 p-3">
        <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-brand">Customer detail</div>
        <div className="grid grid-cols-4 gap-x-3 gap-y-2 text-[11px]">
          <div><div className="text-[9px] uppercase text-ink-muted">Name</div><div className="text-sm font-semibold">{s.patient_name || "—"}</div></div>
          {s.patient_uhid && <div><div className="text-[9px] uppercase text-ink-muted">UHID</div><div className="text-sm font-mono">{s.patient_uhid}</div></div>}
          {s.ipd_no && <div><div className="text-[9px] uppercase text-ink-muted">IPD No.</div><div className="text-sm font-mono">{s.ipd_no}</div></div>}
          {s.patient_mobile && <div><div className="text-[9px] uppercase text-ink-muted">Mobile</div><div className="text-sm">{s.patient_mobile}</div></div>}
          {s.customer_gstin && <div><div className="text-[9px] uppercase text-ink-muted">Customer GSTIN</div><div className="text-sm font-mono">{s.customer_gstin}</div></div>}
          <div><div className="text-[9px] uppercase text-ink-muted">Payment</div><div className="text-sm">{({cash:"Cash",upi:"UPI",card:"Card",credit:"Credit",free:"Free"}[String(s.payment_mode || "").toLowerCase()] || (s.payment_mode || "—").toString())}</div></div>
        </div>
      </div>

      {/* Items table — GST-norm columns */}
      <table className="mt-3 w-full border border-border text-[11px]">
        <thead className="bg-secondary">
          <tr>
            <th className="px-2 py-1 text-left w-7">#</th>
            <th className="px-2 py-1 text-left">Medicine</th>
            <th className="px-2 py-1 text-left w-20">Batch</th>
            <th className="px-2 py-1 text-left w-20">Expiry</th>
            <th className="px-2 py-1 text-left w-16">HSN</th>
            <th className="px-2 py-1 text-right w-10">Qty</th>
            <th className="px-2 py-1 text-right w-20">Rate</th>
            <th className="px-2 py-1 text-right w-16">Disc</th>
            <th className="px-2 py-1 text-right w-16">Taxable</th>
            <th className="px-2 py-1 text-right w-14">CGST</th>
            <th className="px-2 py-1 text-right w-14">SGST</th>
            <th className="px-2 py-1 text-right w-20">Total</th>
          </tr>
        </thead>
        <tbody>
          {s.items.map((it, i) => (
            <tr key={i} className="border-t border-border">
              <td className="px-2 py-1">{i + 1}</td>
              <td className="px-2 py-1">{it.medicine_name}</td>
              <td className="px-2 py-1 font-mono">{it.batch_no || "—"}</td>
              <td className="px-2 py-1">{(it.expiry || "").slice(0, 7)}</td>
              <td className="px-2 py-1 font-mono">{it.hsn_code || "3004"}</td>
              <td className="px-2 py-1 text-right">{it.qty}</td>
              <td className="px-2 py-1 text-right">{(it.rate || 0).toFixed(2)}</td>
              <td className="px-2 py-1 text-right">{(it.discount || 0).toFixed(2)}</td>
              <td className="px-2 py-1 text-right">{(it.taxable || 0).toFixed(2)}</td>
              <td className="px-2 py-1 text-right">{((it.gst_percent || 0) / 2).toFixed(1)}% / {(it.cgst || 0).toFixed(2)}</td>
              <td className="px-2 py-1 text-right">{((it.gst_percent || 0) / 2).toFixed(1)}% / {(it.sgst || 0).toFixed(2)}</td>
              <td className="px-2 py-1 text-right font-semibold">{(it.line_total || 0).toFixed(2)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot className="bg-secondary/60">
          <tr className="border-t border-border font-semibold">
            <td colSpan={8} className="px-2 py-1 text-right">Sub-total</td>
            <td className="px-2 py-1 text-right">{(s.sub_total || 0).toFixed(2)}</td>
            <td className="px-2 py-1 text-right">{(s.cgst_total || 0).toFixed(2)}</td>
            <td className="px-2 py-1 text-right">{(s.sgst_total || 0).toFixed(2)}</td>
            <td className="px-2 py-1 text-right">{(s.total || 0).toFixed(2)}</td>
          </tr>
        </tfoot>
      </table>

      {/* Totals + words */}
      <div className="mt-3 grid grid-cols-2 gap-4">
        <div className="text-[11px]">
          <div className="rounded border border-border bg-secondary/30 p-2">
            <div className="text-[9px] font-semibold uppercase tracking-wider text-ink-muted">Amount in words</div>
            <div className="text-sm italic">Rupees {inWords(s.total || 0)} Only</div>
          </div>
          {s.notes && <div className="mt-2 text-ink-muted"><b>Notes:</b> {s.notes}</div>}
          <div className="mt-3 text-[10px] text-ink-muted">
            Goods once sold cannot be taken back. Subject to local jurisdiction.
            E&amp;OE.
          </div>
        </div>
        <div className="space-y-1 text-sm">
          <div className="flex justify-between"><span className="text-ink-muted">Gross</span><span>₹ {(s.gross || 0).toFixed(2)}</span></div>
          <div className="flex justify-between"><span className="text-ink-muted">Line discount</span><span>− ₹ {(s.line_discount_total || 0).toFixed(2)}</span></div>
          <div className="flex justify-between"><span className="text-ink-muted">Bill discount</span><span>− ₹ {(s.discount_total || 0).toFixed(2)}</span></div>
          <div className="flex justify-between"><span className="text-ink-muted">Taxable</span><span>₹ {(s.sub_total || 0).toFixed(2)}</span></div>
          <div className="flex justify-between"><span className="text-ink-muted">CGST</span><span>+ ₹ {(s.cgst_total || 0).toFixed(2)}</span></div>
          <div className="flex justify-between"><span className="text-ink-muted">SGST</span><span>+ ₹ {(s.sgst_total || 0).toFixed(2)}</span></div>
          <div className="flex justify-between border-t border-border pt-1 font-heading text-base"><span>Grand total</span><span className="text-brand font-bold">₹ {(s.total || 0).toFixed(2)}</span></div>
          {s.payment_mode === "credit" && (
            <>
              <div className="flex justify-between text-xs"><span className="text-ink-muted">Paid</span><span className="text-emerald-700">− ₹ {Number(s.paid_amount || 0).toFixed(2)}</span></div>
              <div className="flex justify-between border-t border-border pt-1 font-heading text-base">
                <span>Outstanding</span>
                <span className={Number(s.balance || 0) > 0 ? "text-rose-700 font-bold" : "text-emerald-700 font-bold"}>₹ {Number(s.balance || 0).toFixed(2)}</span>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Signatures */}
      <div className="mt-12 flex justify-between text-[10px] text-ink-muted">
        <div>Customer / Patient signature</div>
        <div>For {hospital?.name || BRAND.name} · Pharmacist (Reg. No. ………)</div>
      </div>

      <div className="mt-8 border-t border-border pt-3 text-center text-[10px] text-ink-muted">{BRAND.footer}</div>
    </div>
  );
}
